"use client";
import React, { useEffect } from "react";
import isEmpty from "lodash/isEmpty";

import { useFlags, withSyncFeatureFlag } from "@stadium/services/launch-darkly";
import useWindowStorage, { SESSION_STORAGE } from "@stadium/hooks/lib/use-window-storage";

import { LinkButton } from "@stadium/shared/src/components/shared/button";
import AnnouncementBar from "@stadium/ui/src/components/announcement-bar";
import { useAnnouncementBarContext } from "@stadium/shared/src/contexts/announcement-bar";

import { appGenericAnnouncementBarFlag } from "@stadium/services/launch-darkly/constants";

import { isAppGenericAnnouncementBarFlagEnabled } from "@stadium/ui/src/helpers/app-generic-banner";

import styles from "./main.module.scss";

type CtaProps = {
  text: string;
  href: string;
  target?: string;
};

export type AppGenericAnnouncementBarFlagValueType = {
  backgroundColor: string;
  dismissAllowed: boolean;
  endDate: string;
  icon: string;
  message: string;
  startDate: string;
  applications: string[];
  ctaProps?: CtaProps;
  identifier?: string;
  audiences?: string[];
};

const DEFAULT_OBJ = {} as AppGenericAnnouncementBarFlagValueType;

const AppGenericAnnouncementBar = ({
  application,
  options,
}: {
  application: string;
  options: Record<string, any>;
}) => {
  const { [appGenericAnnouncementBarFlag]: appGenericAnnouncementBarFlagValue } =
    useFlags<AppGenericAnnouncementBarFlagValueType>();
  const { setIsClosed: setIsAppGenericBannerClosed } = useAnnouncementBarContext();

  const { backgroundColor, dismissAllowed, icon, message, ctaProps, identifier } =
    appGenericAnnouncementBarFlagValue ?? DEFAULT_OBJ;

  const appGenericAnnouncementBarFlagEnabled = isAppGenericAnnouncementBarFlagEnabled(
    appGenericAnnouncementBarFlagValue,
    application,
    options?.isActivePackageUser,
  );

  const [isClosed, setIsClosed] = useWindowStorage(identifier, false, SESSION_STORAGE);

  const toggleBanner = () => {
    if (dismissAllowed && identifier) {
      setIsClosed(!isClosed);
      setIsAppGenericBannerClosed(!isClosed);
    }
  };

  useEffect(() => {
    setIsAppGenericBannerClosed(isClosed);
  }, [isClosed]);

  if (!appGenericAnnouncementBarFlagEnabled) return null;

  const renderIcon = () => {
    if (icon === "info") return <span className={styles.infoIcon} />;
    return null;
  };

  return (
    !isClosed && (
      <AnnouncementBar
        color={backgroundColor ?? "blue"}
        showCross={dismissAllowed}
        className={styles.bannerContainer}
        handleOnCloseClick={toggleBanner}
      >
        {icon && renderIcon()}
        {message}
        {!isEmpty(ctaProps) && (
          <LinkButton
            href={ctaProps.href}
            className={styles.announcementBarCta}
            styleType="link"
            target={ctaProps.target ?? "_self"}
          >
            <span className={styles.announcementBarCtaText}>{ctaProps.text}</span>
            {ctaProps.target === "_blank" && (
              <span className={styles.announcementBarIconExternal} />
            )}
          </LinkButton>
        )}
      </AnnouncementBar>
    )
  );
};

export default withSyncFeatureFlag(AppGenericAnnouncementBar);
